/**
 * NewSessionCreator — extracted from LiveHostPage.jsx for readability.
 *
 * Renders the "New Live session" button → expandable card with two source
 * tabs: from a module section, or sampled from a question bank. On success,
 * calls `onCreated(session)` and resets/closes itself.
 *
 * Owns all of its own state — no props for the form fields. The parent only
 * needs to consume the result via `onCreated`.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Plus, Library, FileText, Mail, MailX, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { BankUnpublishedHint } from '../banks/BankUnpublishedHint';

export default function NewSessionCreator({ onCreated }) {
  const { post, get } = useApi();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('module'); // 'module' | 'bank'

  // Module-source state
  const [modules, setModules] = useState([]);
  const [pickedModuleId, setPickedModuleId] = useState('');
  const [moduleDetail, setModuleDetail] = useState(null);
  const [loadingModule, setLoadingModule] = useState(false);
  const [pickedSectionId, setPickedSectionId] = useState('');

  // Bank-source state
  const [banks, setBanks] = useState([]);
  const [pickedBankId, setPickedBankId] = useState('');
  const [sampleSize, setSampleSize] = useState(10);
  const [bankDifficulty, setBankDifficulty] = useState('');
  const [bankLanguage, setBankLanguage] = useState('');
  const [bankTags, setBankTags] = useState([]); // available tags for picked bank
  const [pickedTags, setPickedTags] = useState([]); // filter selection

  // Shared
  const [requireEmail, setRequireEmail] = useState(true);
  // Anti-cheat settings
  const [oneJoinPerDevice, setOneJoinPerDevice] = useState(true);
  const [restrictRoster, setRestrictRoster] = useState(false);
  const [rosterBatchId, setRosterBatchId] = useState('');
  const [rosterBatches, setRosterBatches] = useState(null);

  const antiCheatPayload = () => ({
    one_join_per_device: oneJoinPerDevice,
    restrict_to_roster: restrictRoster,
    roster_batch_id: restrictRoster && rosterBatchId ? rosterBatchId : null,
  });

  const loadRosterBatches = async () => {
    if (rosterBatches !== null) return;
    try {
      const res = await get('/trainees/batches');
      setRosterBatches(res?.batches || []);
    } catch {
      setRosterBatches([]);
    }
  };
  const [busy, setBusy] = useState(false);

  // Load published modules + banks once the creator opens
  useEffect(() => {
    if (!open) return;
    get('/modules?limit=200&status=published')
      .then((r) => setModules(r.modules || r || []))
      .catch(() => setModules([]));
    // Banks: list everything; we filter to lifecycle_status=published in the dropdown
    get('/question-banks?limit=200')
      .then((r) => setBanks((r.banks || []).filter(b => b.lifecycle_status === 'published')))
      .catch(() => setBanks([]));
  }, [open, get]);

  // When module changes, fetch its sections to populate the section picker
  useEffect(() => {
    if (!pickedModuleId) { setModuleDetail(null); setPickedSectionId(''); return; }
    setLoadingModule(true);
    setPickedSectionId('');
    get(`/modules/${pickedModuleId}`)
      .then(setModuleDetail)
      .catch(() => setModuleDetail(null))
      .finally(() => setLoadingModule(false));
  }, [pickedModuleId, get]);

  // When bank changes, fetch distinct tags so we can offer them as filter chips
  useEffect(() => {
    setPickedTags([]);
    if (!pickedBankId) { setBankTags([]); return; }
    get(`/question-banks/${pickedBankId}/tags`)
      .then((r) => setBankTags(r.tags || []))
      .catch(() => setBankTags([]));
  }, [pickedBankId, get]);

  const quizSections = (moduleDetail?.sections || []).filter(s => s.section_type === 'quiz' && s.quiz_id);
  const pickedSection = quizSections.find(s => s.section_id === pickedSectionId);
  const pickedBank = banks.find(b => b.bank_id === pickedBankId);
  const bankPoolSize = pickedBank?.published_item_count ?? pickedBank?.item_count ?? 0;
  const sampleSizeValid = bankPoolSize > 0 && sampleSize >= 1 && sampleSize <= bankPoolSize && sampleSize <= 100;

  const resetAndClose = () => {
    setOpen(false);
    setPickedModuleId(''); setPickedSectionId(''); setModuleDetail(null);
    setPickedBankId(''); setSampleSize(10); setBankDifficulty(''); setBankLanguage('');
    setBankTags([]); setPickedTags([]);
  };

  const createFromModule = async () => {
    if (!pickedSection) { toast.error('Pick a quiz section'); return; }
    setBusy(true);
    try {
      const res = await post('/live/sessions', {
        quiz_id: pickedSection.quiz_id,
        mode: 'section_run',
        target_module_id: pickedModuleId,
        target_section_id: pickedSectionId,
        require_participant_email: requireEmail,
        ...antiCheatPayload(),
      });
      toast.success(`Session ready · code ${res.ref_code}`);
      resetAndClose();
      onCreated(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const createFromBank = async () => {
    if (!pickedBank) { toast.error('Pick a question bank'); return; }
    if (!sampleSizeValid) { toast.error('Adjust the sample size'); return; }
    setBusy(true);
    try {
      const payload = {
        bank_id: pickedBankId,
        sample_size: Number(sampleSize),
        require_participant_email: requireEmail,
        ...antiCheatPayload(),
      };
      if (bankDifficulty) payload.difficulty = bankDifficulty;
      if (bankLanguage) payload.language = bankLanguage;
      if (pickedTags.length > 0) payload.tags = pickedTags;
      const res = await post('/live/sessions/from-bank', payload);
      toast.success(`Session ready · code ${res.ref_code}`);
      resetAndClose();
      onCreated(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const create = () => source === 'module' ? createFromModule() : createFromBank();
  const canCreate = source === 'module' ? !!pickedSection : (!!pickedBank && sampleSizeValid);

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} data-testid="new-session-btn">
        <Plus className="w-4 h-4 mr-1" />{'New Live session'}
      </Button>
    );
  }

  return (
    <Card data-testid="session-creator">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Create a Live session</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {/* Source tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-md text-xs" data-testid="source-tabs">
          <button
            type="button"
            onClick={() => setSource('module')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded transition ${source === 'module' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="source-module"
          >
            <FileText className="w-3.5 h-3.5" />From a module section
          </button>
          <button
            type="button"
            onClick={() => setSource('bank')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded transition ${source === 'bank' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="source-bank"
          >
            <Library className="w-3.5 h-3.5" />From a question bank
          </button>
        </div>

        {/* ── Module source ── */}
        {source === 'module' && (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Module</label>
              <select
                className="w-full text-sm p-2 rounded-md border bg-background"
                value={pickedModuleId}
                onChange={(e) => setPickedModuleId(e.target.value)}
                data-testid="module-select"
              >
                <option value="">— select a published module —</option>
                {Array.isArray(modules) && modules.map((m) => (
                  <option key={m.module_id} value={m.module_id}>{m.title}</option>
                ))}
              </select>
              {modules.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  No published modules yet. Publish a module from Content first.
                </p>
              )}
            </div>

            {pickedModuleId && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Quiz section</label>
                <select
                  className="w-full text-sm p-2 rounded-md border bg-background"
                  value={pickedSectionId}
                  onChange={(e) => setPickedSectionId(e.target.value)}
                  disabled={loadingModule || quizSections.length === 0}
                  data-testid="section-select"
                >
                  <option value="">
                    {loadingModule
                      ? 'Loading sections…'
                      : quizSections.length === 0
                        ? '— this module has no quiz sections —'
                        : '— select a quiz section —'}
                  </option>
                  {quizSections.map((s) => {
                    const n = s.total_questions ?? 0;
                    const suffix = n === 0 ? ' — no questions yet' : ` · ${n} question${n === 1 ? '' : 's'}`;
                    return (
                      <option key={s.section_id} value={s.section_id} disabled={n === 0}>
                        {s.title}{suffix}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </>
        )}

        {/* ── Bank source ── */}
        {source === 'bank' && (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Question bank</label>
              <select
                className="w-full text-sm p-2 rounded-md border bg-background"
                value={pickedBankId}
                onChange={(e) => setPickedBankId(e.target.value)}
                data-testid="bank-select"
              >
                <option value="">— select a published bank —</option>
                {banks.map((b) => {
                  const n = b.published_item_count ?? b.item_count ?? 0;
                  const u = b.unpublished_item_count || 0;
                  return (
                    <option key={b.bank_id} value={b.bank_id} disabled={n === 0 && u === 0}>
                      {b.title} · {n} published{u > 0 ? ` · ${u} unpublished` : ` item${n === 1 ? '' : 's'}`}
                    </option>
                  );
                })}
              </select>
              {banks.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  No published banks yet. Publish a bank from Question Banks first.
                </p>
              )}
              <BankUnpublishedHint
                bank={pickedBank}
                onPublished={(updated) => setBanks(prev => prev.map(b => b.bank_id === updated.bank_id ? { ...b, ...updated } : b))}
              />
            </div>

            {pickedBank && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Sample size</label>
                    <input
                      type="number"
                      min={1}
                      max={Math.min(100, bankPoolSize || 1)}
                      value={sampleSize}
                      onChange={(e) => setSampleSize(Number(e.target.value) || 1)}
                      className="w-full text-sm p-2 rounded-md border bg-background"
                      data-testid="sample-size"
                    />
                    <p className="text-[10px] text-muted-foreground">Pool: {bankPoolSize}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Difficulty</label>
                    <select
                      value={bankDifficulty}
                      onChange={(e) => setBankDifficulty(e.target.value)}
                      className="w-full text-sm p-2 rounded-md border bg-background"
                      data-testid="bank-difficulty"
                    >
                      <option value="">Any</option>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Language</label>
                    <select
                      value={bankLanguage}
                      onChange={(e) => setBankLanguage(e.target.value)}
                      className="w-full text-sm p-2 rounded-md border bg-background"
                      data-testid="bank-language"
                    >
                      <option value="">Any</option>
                      <option value="en">English</option>
                      <option value="ar">Arabic</option>
                    </select>
                  </div>
                </div>
                {bankTags.length > 0 && (
                  <div className="space-y-1" data-testid="bank-tags-filter">
                    <label className="text-xs font-medium text-muted-foreground">
                      Filter by tags {pickedTags.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setPickedTags([])}
                          className="ml-2 text-[10px] text-muted-foreground hover:text-foreground underline"
                          data-testid="clear-tags"
                        >
                          clear
                        </button>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {bankTags.map((t) => {
                        const on = pickedTags.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            data-testid={`bank-tag-${t}`}
                            onClick={() => setPickedTags(
                              on ? pickedTags.filter(x => x !== t) : [...pickedTags, t],
                            )}
                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition ${
                              on
                                ? 'bg-[var(--lumyn-banks)]/15 text-[var(--lumyn-banks)] border-[var(--lumyn-banks)]/40'
                                : 'bg-background text-muted-foreground border-border hover:border-[var(--lumyn-banks)]/40'
                            }`}
                          >
                            <Tag className="w-2.5 h-2.5" />{t}
                          </button>
                        );
                      })}
                    </div>
                    {pickedTags.length > 0 && (
                      <p className="text-[10px] text-muted-foreground">
                        Sampling will be restricted to items tagged with at least one of the selected tags.
                      </p>
                    )}
                  </div>
                )}
                {!sampleSizeValid && (
                  <p className="text-[11px] text-rose-500" data-testid="sample-size-error">
                    {bankPoolSize === 0
                      ? 'This bank has no published questions yet — publish its items to draw from it.'
                      : `Sample size must be between 1 and ${Math.min(100, bankPoolSize)}.`}
                  </p>
                )}
              </>
            )}
          </>
        )}

        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <input
            type="checkbox"
            checked={requireEmail}
            onChange={(e) => setRequireEmail(e.target.checked)}
            data-testid="require-email-toggle"
          />
          Require participant email
          {requireEmail
            ? <Mail className="w-3 h-3 text-muted-foreground" />
            : <MailX className="w-3 h-3 text-muted-foreground" />}
        </label>

        {/* Anti-cheat settings */}
        <div className="rounded-md border border-dashed px-3 py-2 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Anti-cheat</p>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={oneJoinPerDevice}
              onChange={(e) => setOneJoinPerDevice(e.target.checked)}
              data-testid="one-join-per-device-toggle"
            />
            One join per device (re-joining resumes the same participant)
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={restrictRoster}
              onChange={(e) => {
                setRestrictRoster(e.target.checked);
                if (e.target.checked) loadRosterBatches();
              }}
              data-testid="restrict-roster-toggle"
            />
            Restrict to batch roster (email must match a trainee)
          </label>
          {restrictRoster && (
            <select
              className="w-full border rounded-md px-2 py-1.5 bg-background text-xs"
              value={rosterBatchId}
              onChange={(e) => setRosterBatchId(e.target.value)}
              data-testid="roster-batch-select"
            >
              <option value="">Any batch in your org</option>
              {(rosterBatches || []).map(b => (
                <option key={b.batch_id} value={b.batch_id}>{b.name}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={create} disabled={busy || !canCreate} size="sm" data-testid="create-session-btn">
            Create
          </Button>
          <Button variant="ghost" onClick={resetAndClose} size="sm">Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}
